import React, { useState, useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import { IconArrowDown } from '../_svgs/ArrowDown/ArrowDown'
import useAmplitude from '~/hooks/useAmplitude'

export const FooterLinks = () => {
  const { trackHeaderFooterClicked } = useAmplitude()
  const [openIndexes, setOpenIndexes] = useState([])
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 767)
  const resizeTimer = useRef(null)

  useEffect(() => {
    const handleResize = () => {
      if (resizeTimer.current !== null) {
        cancelAnimationFrame(resizeTimer.current)
      }

      resizeTimer.current = requestAnimationFrame(() => {
        setIsMobile(window.innerWidth <= 767)
      })
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      if (resizeTimer.current !== null) {
        cancelAnimationFrame(resizeTimer.current)
      }
    }
  }, [])

  const toggleAccordion = (index) => {
    if (!isMobile) return
    setOpenIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    )
  }

  return (
    <FooterLinksRow>
      <FooterLinksContainer>
        {columns.map((column, index) => {
          const isOpen = openIndexes.includes(index)

          return (
            <FooterLinksColumn key={index}>
              <FooterLinksHead
                as={isMobile ? 'button' : column.header.href ? 'a' : 'span'}
                $asSpan={!isMobile && !column.header.href}
                href={isMobile ? undefined : column.header.href}
                onClick={(e) => {
                  if (isMobile) {
                    e.preventDefault()
                    toggleAccordion(index)
                  } else {
                    trackHeaderFooterClicked(column.header.trackedData)
                  }
                }}
                $isOpen={isOpen}
              >
                {column.header.title}
                {isMobile && (
                  <IconContainer>
                    <IconArrowDown
                      width='14px'
                      color='white'
                      point={isOpen ? 'down' : undefined}
                    />
                  </IconContainer>
                )}
              </FooterLinksHead>

              <FooterLinksListWrapper
                $isOpen={isOpen}
                id={`footer-accordion-${index}`}
              >
                <FooterLinksList>
                  {column.header.title === 'Help' &&
                      <FooterLinksListItem>
                        <FooterLinksListItemButton tabIndex={0} id='accessibilityWidget'>Accessibility Support</FooterLinksListItemButton>
                      </FooterLinksListItem>
                  }
                  {column.links.map((link) => (
                    <FooterLinksListItem key={link.title}>
                      <FooterLinksListItemA
                        href={link.href}
                        onClick={() => {
                          trackHeaderFooterClicked(link.trackedData)
                        }}
                      >
                        {link.title}
                      </FooterLinksListItemA>
                    </FooterLinksListItem>
                  ))}
                </FooterLinksList>
              </FooterLinksListWrapper>
            </FooterLinksColumn>
          )
        })}
      </FooterLinksContainer>
    </FooterLinksRow>
  )
}

const columns = [
  {
    header: {
      title: 'Orders',
      href: '/myaccount/orders.html',
      trackedData: {
        L1: 'Orders',
        HeaderFooterType: 'footer',
        HeaderFooterLink: '/myaccount/orders.html',
      },
    },
    links: [
      {
        title: 'My Account',
        href: '/myaccount',
        trackedData: {
          L1: 'Order',
          L2: 'My Account',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/myaccount',
        },
      },
      {
        title: 'Order Information',
        href: '/myaccount/orders.html',
        trackedData: {
          L1: 'Order',
          L2: 'Order Information',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/myaccount/orders.html',
        },
      },
      {
        title: 'Shipping Details',
        href:
          '/pages/shipping-methods-and-rates',
        trackedData: {
          L1: 'Order',
          L2: 'Shipping Details',
          HeaderFooterType: 'footer',
          HeaderFooterLink:
            '/pages/shipping-methods-and-rates',
        },
      },
      {
        title: 'International Information',
        href: '/pages/landing-pages-international',
        trackedData: {
          L1: 'Order',
          L2: 'International Information',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/pages/landing-pages-international',
        },
      },
    ],
  },
  {
    header: {
      title: 'Help',
      href: '/pages/help',
      trackedData: {
        L1: 'Help',
        HeaderFooterType: 'footer',
        HeaderFooterLink: '/pages/help',
      },
    },
    links: [
      {
        title: 'Guarantees',
        href: '/pages/guarantees',
        trackedData: {
          L1: 'Help',
          L2: 'Guarantees',
          HeaderFooterType: 'footer',
          HeaderFooterLink:
            '/pages/guarantees',
        },
      },
      {
        title: 'Product Information',
        href: '/pages/product-information',
        trackedData: {
          L1: 'Help',
          L2: 'Product Information',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/pages/product-information',
        },
      },
      {
        title: 'Returns',
        href: '/pages/returns',
        trackedData: {
          L1: 'Help',
          L2: 'Returns',
          HeaderFooterType: 'footer',
          HeaderFooterLink:
            '/pages/returns',
        },
      },
      {
        title: 'Security & Privacy',
        href:
          '/pages/security-and-privacy-information',
        trackedData: {
          L1: 'Help',
          L2: 'Security & Privacy',
          HeaderFooterType: 'footer',
          HeaderFooterLink:
            '/pages/security-and-privacy-information',
        },
      },
      {
        title: 'FAQs',
        href: '/pages/10-faqs',
        trackedData: {
          L1: 'Help',
          L2: 'FAQs',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/pages/10-faqs',
        },
      },
    ],
  },
  {
    header: {
      title: 'About Us',
      href: '/pages/landing-pages-celebrate',
      trackedData: {
        L1: 'About Us',
        HeaderFooterType: 'footer',
        HeaderFooterLink: '/pages/landing-pages-celebrate',
      },
    },
    links: [
      {
        title: 'History',
        href: '/pages/about-swanson',
        trackedData: {
          L1: 'About Us',
          L2: 'History',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/pages/about-swanson',
        },
      },
      {
        title: 'Social Responsibility',
        href:
          '/pages/corporate-social-responsibility',
        trackedData: {
          L1: 'About Us',
          L2: 'Social Responsibility',
          HeaderFooterType: 'footer',
          HeaderFooterLink:
            '/pages/corporate-social-responsibility',
        },
      },
      {
        title: 'Swanson Cares',
        href: '/pages/landing-pages-swansoncares',
        trackedData: {
          L1: 'About Us',
          L2: 'Swanson Cares',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/pages/landing-pages-swansoncares',
        },
      },
      {
        title: 'Blog',
        href: '/blogs/',
        trackedData: {
          L1: 'About Us',
          L2: 'Blog',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/blogs/',
        },
      },
      {
        title: 'Careers',
        href: '/career',
        trackedData: {
          L1: 'About Us',
          L2: 'Careers',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/career',
        },
      },
    ],
  },
  {
    header: {
      title: 'Services',
      href: '',
    },
    links: [
      {
        title: 'Wellness Rewards',
        href: '/myaccount/rewards.html',
        trackedData: {
          L1: 'Services',
          L2: 'Wellness Rewards',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/myaccount/rewards.html',
        },
      },
      {
        title: 'Easy Refill',
        href: '/myaccount/easyrefill.html',
        trackedData: {
          L1: 'Services',
          L2: 'Easy Refill',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/myaccount/easyrefill.html',
        },
      },
      {
        title: 'Favorites',
        href: '/myaccount/favorites.html',
        trackedData: {
          L1: 'Services',
          L2: 'Favorites',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/myaccount/favorites.html',
        },
      },
      {
        title: 'Request a Catalog',
        href: '/pages/catalogrequest',
        trackedData: {
          L1: 'Services',
          L2: 'Request a Catalog',
          HeaderFooterType: 'footer',
          HeaderFooterLink: '/pages/catalogrequest',
        },
      },
    ],
  },
]

const FooterLinksRow = styled.div`
  background-color: ${(props) => props.theme.darkGreen};
  display: flex;
  align-items: center;
  @media (min-width: 768px) {
    border-bottom: 1px solid rgba(217, 217, 217, 0.5);
  }
`

const FooterLinksContainer = styled.div`
  display: flex;
  max-width: 1420px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  padding-bottom: 34px;
  padding-top: 32px;
  gap: 20px;
  @media (max-width: 1420px) {
    padding-left: 15px;
    padding-right: 15px;
  }
  @media (max-width: 767px) {
    padding-bottom: 15px;
    flex-direction: column;
    gap: 0;
    padding-top: 0;
  }
`

const FooterLinksColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 25%;
  color: ${(props) => props.theme.white};

  @media (max-width: 767px) {
    width: 100%;

    &:last-child {
      border-bottom: 1px solid rgba(217, 217, 217, 0.5);
    }
  }
`

const FooterLinksHead = styled.a`
  cursor: pointer;
  font-size: 20px;
  line-height: 26px;
  font-weight: 700;
  background: none;
  border: none;
  text-align: left;
  padding: 0;
  color: ${(props) => props.theme.white} !important;
  text-decoration: none;

  &:focus {
    outline: none;
    text-decoration: underline !important;
    text-decoration-thickness: 1px !important;
    text-underline-offset: 3px;
    color: ${(props) => props.theme.white};
  }

  &:hover,
  &:active {
    color: ${(props) => props.theme.white} !important;
    text-decoration: none !important;
  }

  @media (min-width: 768px) {
    width: fit-content;
    &:hover {
      text-decoration: underline !important;
      text-decoration-thickness: 1px !important;
      text-underline-offset: 3px;
    }
  }

  @media (min-width: 1420px) {
    font-size: 28px;
  }

  @media (max-width: 767px) {
    font-size: 18px;
    line-height: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 17px;
    padding-bottom: 18px;
    border-top: 1px solid rgba(217, 217, 217, 0.5);
  }

  ${(props) =>
    props.$asSpan &&
    css`
      cursor: default;
      &:hover {
        text-decoration: none !important;
      }
    `}
`

const FooterLinksListWrapper = styled.div`
  overflow: hidden;
  transition: max-height 0.3s ease;
  max-height: 999px;

  @media (max-width: 767px) {
    max-height: ${(props) => (props.$isOpen ? '500px' : '0')};
  }
`

const FooterLinksList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin: 10px 0 0 0;
  list-style: none;
  padding: 0;

  @media (max-width: 767px) {
    padding-bottom: 12px;
    margin-top: 0;
    gap: 0;
  }
`

const FooterLinksListItem = styled.li`
  margin: 0;
`

const FooterLinksListItemA = styled.a`
  cursor: pointer;
  font-size: 16px;
  line-height: 22px;
  font-weight: 400;
  color: ${(props) => props.theme.white} !important;
  text-decoration: none;

  &:hover {
    color: ${(props) => props.theme.white} !important;
    text-decoration-thickness: 1px !important;
    text-underline-offset: 3px;
  }

  &:focus {
    outline: none;
    text-decoration: underline !important;
    text-decoration-thickness: 1px !important;
    text-underline-offset: 3px;
    color: ${(props) => props.theme.white};
  }

  @media (min-width: 1420px) {
    font-size: 20px;
  }

  @media (max-width: 767px) {
    display: flex;
    padding-top: 6px;
    padding-bottom: 6px;
  }
`

const IconContainer = styled.span`
  @media (min-width: 768px) {
    display: none;
  }
`

const FooterLinksListItemButton = styled.button`
  cursor: pointer;
  font-size: 16px;
  line-height: 22px;
  font-weight: 400;
  color: ${(props) => props.theme.white} !important;
  background: transparent;
  text-decoration: none;
  border: none;
  padding: 0;

  &:hover {
    color: ${(props) => props.theme.white} !important;
    text-decoration-thickness: 1px !important;
    text-underline-offset: 3px;
    text-decoration: underline;
  }

  &:focus {
    outline: none;
    text-decoration: underline !important;
    text-decoration-thickness: 1px !important;
    text-underline-offset: 3px;
    color: ${(props) => props.theme.white};
  }

  @media (min-width: 1420px) {
    font-size: 20px;
  }

  @media (max-width: 767px) {
    display: flex;
    padding-top: 6px;
    padding-bottom: 6px;
  }
`
